import React, {useState} from "react";
import {GameGrid} from "./GameGrid/GameGrid";
import {DragEventsType, DragItemType} from "../types";
import {initialGridData} from "../utils/gameUtils/initialGridData";
import {createRandomDragItem} from "../utils/gameUtils/getInitialDragData";
import {putItemOnGrid} from "../utils/gameUtils/putItemOnGrid";
import {useSizeItem} from "../provider/ResizeProvider";
import {Score} from "./Scoring/Score";
import styled from "styled-components";
import {GameOverModal} from "./Modals/GameOverModal";
import {SettingsModal} from "../settings/SettingsModal";
import {useAppContext} from "../provider/AppProvider";
import {dataStorage} from "../utils/StorageService";
import {HintModal} from "./Modals/HintModal";
import {useShowPossiblePositionHint} from "../hooks/game_hooks/useShowPossiblePositionHint";
import {useSoundToggle} from "../hooks/useSoundToggle";
import {useCheckEmptyGridData} from "../hooks/game_hooks/useCheckEmptyGridData";
import {useGameOverEffect} from "../hooks/game_hooks/useGameOverEffect";
import {useGridDataByMode} from "../hooks/game_hooks/useGridDataByMode";
import {UploadBombSprites} from "./uploadBombAnimation/UploadBombSprites";
import {useSaveDataScoreChanged} from "../hooks/game_hooks/useSaveDataScoreChanged";

const Container = styled.div`
    height: 100%;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
`;

const Game = () => {
    const {playSound: playSoundCombo} = useSoundToggle('combo');
    const {playSound: playSoundBomb} = useSoundToggle('bomb', 0.2);
    const [isHintWasShowing, setHintShowing] = useState(dataStorage.getItem()?.hint_was_showing || false);
    const {screen, settings, setIdeaProcessing} = useAppContext();
    const [isGameOver, setGameOver] = useState(false);
    const {itemSize, panelItemSize} = useSizeItem();
    const [dragItem, setDragItem] = useState<DragItemType | null>(null);
    const [videoWasWatched, setVideoWatched] = useState(dataStorage.getItem()?.video_watch_data || false);
    const [score, setScore] = useState(dataStorage.getItem()?.score_data || 0);
    const [totalScore, setTotalScore] = useState(dataStorage.getItem()?.total_score_data || 0);
    const {gridData, setGridData} = useGridDataByMode();
    const [dragItems, setDragItems] = useState(dataStorage.getItem()?.tools_panel || [
        createRandomDragItem(panelItemSize, 0),
        createRandomDragItem(panelItemSize, 1),
        createRandomDragItem(panelItemSize, 2),
    ])

    useShowPossiblePositionHint({dragItems, setGridData, gridData});

    useCheckEmptyGridData({gridData, setDragItems});

    useGameOverEffect({setGameOver, score, totalScore, setTotalScore, dragItems, gridData})

    useSaveDataScoreChanged({score, gridData, setIdeaProcessing, dragItems});

    const handleRemoveRows = (count: number) => {
        setVideoWatched(true);
        const clearGridData = initialGridData(itemSize);
        const newDragItems = [
            createRandomDragItem(panelItemSize, 0),
            createRandomDragItem(panelItemSize, 1),
            createRandomDragItem(panelItemSize, 2),
        ];

        const data = [...clearGridData.slice(0, count), ...gridData.slice(count)];

        if (dataStorage.isBombMode) {
            for (let i = 0; i < data.length; i++) {
                for (let j = 0; j < data[i].length; j++) {
                    const itemOnGrid = data[i][j].item;

                    if (itemOnGrid && itemOnGrid.typeOfItem === 'BOMB') {
                        data[i][j].item = undefined;
                    }
                }
            }
        }

        setGridData(data);

        dataStorage.setItem({
            video_watch_data: true,
            grid_data: data,
            tools_panel: newDragItems,
        });

        setDragItems(newDragItems)
        setGameOver(false);
    }

    const handleRestartGame = () => {
        setDragItems([
            createRandomDragItem(panelItemSize, 0),
            createRandomDragItem(panelItemSize, 1),
            createRandomDragItem(panelItemSize, 2),
        ])
        setGridData(initialGridData(itemSize))
        setScore(0);
        setGameOver(false);
        setVideoWatched(false);
        dataStorage.setItem({
            grid_data: undefined,
            tools_panel: undefined,
            score_data: 0,
            video_watch_data: false,
        })
    }

    const handleDragEvent = (typeEvent: DragEventsType, over: string) => {
        if (!dragItem || dragItem.indexToolPanel === undefined) return;
        putItemOnGrid(
            gridData,
            dragItem,
            over,
            setGridData,
            typeEvent,
            setDragItems,
            setScore,
            panelItemSize,
            playSoundCombo,
            itemSize,
            playSoundBomb,
        );
    }

    return (
        <Container>
            <Score score={score} totalScore={totalScore}/>
            <GameGrid
                gridData={gridData}
                dragItems={dragItems}
                setDragItem={setDragItem}
                onDragEvent={handleDragEvent}
            />
            <GameOverModal
                videoWasWatched={videoWasWatched}
                isGameOver={isGameOver}
                totalScore={totalScore}
                score={score}
                restart={handleRestartGame}
                removeRows={handleRemoveRows}
            />
            {screen === 'game' && <SettingsModal onShowHint={() => {
                setHintShowing(false);
            }} restart={handleRestartGame} isOpen={settings}/>}
            {!isHintWasShowing && (
                <HintModal isOpen={!isHintWasShowing} onClose={() => {
                    setHintShowing(true);
                    dataStorage.setItem({hint_was_showing: true})
                }}/>
            )}
            <UploadBombSprites />
        </Container>
    )
}

export default React.memo(Game);
